import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useMixpanel } from "./mixpanel-context";
import { MixpanelEventProperties } from "./mixpanel-events";

interface UsePageTrackingOptions {
  pageName?: string;
  additionalProperties?: MixpanelEventProperties;
  trackOnMount?: boolean;
  trackOnLocationChange?: boolean;
}

export function usePageTracking(options: UsePageTrackingOptions = {}) {
  const { pageName, additionalProperties, trackOnMount = true, trackOnLocationChange = true } = options;

  const { trackPageView } = useMixpanel();
  const location = useLocation();
  const previousLocation = useRef(location.pathname);
  const mountTime = useRef(Date.now());

  const getPageName = () => {
    if (pageName) return pageName;

    const path = location.pathname;
    if (path === "/") return "Overview";

    const segments = path.split("/").filter(Boolean);
    return segments.map(segment => segment.charAt(0).toUpperCase() + segment.slice(1)).join(" > ");
  };

  const getQueryParams = () => {
    const searchParams = new URLSearchParams(location.search);
    const params: Record<string, string> = {};

    searchParams.forEach((value, key) => {
      params[key] = value;
    });

    return Object.keys(params).length > 0 ? params : undefined;
  };

  const getFullPath = () => {
    return location.pathname + location.search;
  };

  useEffect(() => {
    if (trackOnMount) {
      const queryParams = getQueryParams();
      const trackingProps = {
        ...additionalProperties,
        page_load_time: Date.now() - mountTime.current,
        ...(queryParams && { query_params: queryParams }),
      };
      trackPageView(getPageName(), getFullPath(), trackingProps);
    }
  }, [trackOnMount]);

  useEffect(() => {
    if (trackOnLocationChange && location.pathname !== previousLocation.current) {
      const queryParams = getQueryParams();
      const trackingProps = {
        ...additionalProperties,
        previous_page: previousLocation.current,
        ...(queryParams && { query_params: queryParams }),
      };
      trackPageView(getPageName(), getFullPath(), trackingProps);
      previousLocation.current = location.pathname;
    }
  }, [location.pathname, trackOnLocationChange, additionalProperties]);

  return {
    currentPage: getPageName(),
    currentPath: location.pathname,
  };
}

export function useInteractionTracking() {
  const { trackFeature } = useMixpanel();

  const trackFeatureUsage = useCallback(
    (
      featureName: string,
      actionType: "click" | "view" | "filter" | "search" | "export" | "download",
      additionalProps?: MixpanelEventProperties,
    ) => {
      trackFeature(featureName, actionType, {
        ...additionalProps,
      });
    },
    [trackFeature],
  );

  return {
    trackFeatureUsage,
  };
}

export function useFilterTracking() {
  const { trackFilterChanged } = useMixpanel();
  const previousValuesRef = useRef<Record<string, unknown>>({});

  const trackFilterChange = useCallback(
    <T>(filterType: string, newValue: T) => {
      const previousValue = previousValuesRef.current[filterType] as T | undefined;

      trackFilterChanged(filterType, newValue, previousValue);

      previousValuesRef.current[filterType] = newValue;
    },
    [trackFilterChanged],
  );

  return { trackFilterChange };
}
